import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Newspaper, Folder, GraduationCap, Briefcase, Wrench, Heart, Kanban, ChatCircle, Plus, Trash, Camera, Link as LinkIcon, FileText, ArrowSquareOut, ChatText, PaperPlaneTilt, X, Users, PencilSimple } from "@phosphor-icons/react";

// ===================== QUICK ACCESS ICONS =====================
export function QuickAccessBar({ onNavigate }) {
  const items = [
    { id: "news", label: "Noticias", icon: Newspaper, color: "bg-blue-500" },
    { id: "docs", label: "Documentos", icon: Folder, color: "bg-amber-500" },
    { id: "training", label: "Formacion", icon: GraduationCap, color: "bg-violet-500" },
    { id: "services", label: "Servicios", icon: Briefcase, color: "bg-emerald-500" },
    { id: "tools", label: "Herramientas", icon: Wrench, color: "bg-slate-500" },
    { id: "wellness", label: "Bienestar", icon: Heart, color: "bg-rose-500" },
    { id: "projects", label: "Proyectos", icon: Kanban, color: "bg-cyan-500" },
    { id: "forum", label: "Foro", icon: ChatCircle, color: "bg-orange-500" },
  ];
  return (
    <div className="grid grid-cols-4 md:grid-cols-8 gap-3" data-testid="quick-access-bar">
      {items.map(item => (
        <button key={item.id} onClick={() => onNavigate(item.id)} className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-slate-50 transition-colors group" data-testid={`qa-${item.id}`}>
          <div className={`w-11 h-11 ${item.color} rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
            <item.icon size={20} className="text-white" weight="fill" />
          </div>
          <span className="text-[11px] font-semibold text-slate-600 group-hover:text-slate-900">{item.label}</span>
        </button>
      ))}
    </div>
  );
}

// ===================== NEWS SECTION =====================
export function NewsSection() {
  const { token, user } = useAuth();
  const isAdmin = ["admin", "auxiliar"].includes(user?.role);
  const [news, setNews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectedNews, setSelectedNews] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/intranet/news`, { headers }); setNews(data || []); }
    catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!title.trim()) return toast.error("Titulo requerido");
    try { await axios.post(`${API}/intranet/news`, { title, content }, { headers }); toast.success("Noticia publicada"); setTitle(""); setContent(""); setShowForm(false); load(); }
    catch (e) { toast.error("Error"); }
  };

  const handleUploadCover = async (newsId, e) => {
    const file = e.target.files[0]; if (!file) return;
    try { const fd = new FormData(); fd.append("file", file); await axios.post(`${API}/intranet/news/${newsId}/cover`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } }); toast.success("Imagen subida"); load(); }
    catch (er) { toast.error("Error al subir"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta noticia?")) return;
    try { await axios.delete(`${API}/intranet/news/${id}`, { headers }); toast.success("Eliminada"); setSelectedNews(null); load(); } catch (e) { toast.error("Error"); }
  };

  const handleEdit = (n) => {
    setEditingNews(n.id);
    setEditTitle(n.title);
    setEditContent(n.content || "");
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) return toast.error("Titulo requerido");
    try {
      await axios.put(`${API}/intranet/news/${editingNews}`, { title: editTitle, content: editContent }, { headers });
      toast.success("Noticia actualizada");
      setEditingNews(null);
      setSelectedNews(null);
      load();
    } catch (e) { toast.error("Error al actualizar"); }
  };

  // ===== DETAIL VIEW (all users can see) =====
  if (selectedNews) {
    const n = selectedNews;
    return (
      <div className="bg-white rounded-xl border overflow-hidden" data-testid="news-detail" id="section-news">
        <div className="bg-blue-600 px-5 py-3 flex items-center justify-between">
          <h3 className="text-white font-bold flex items-center gap-2"><Newspaper size={18} weight="fill" /> Noticias y Comunicaciones</h3>
          <button onClick={() => { setSelectedNews(null); setEditingNews(null); }} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full" data-testid="news-back">Volver</button>
        </div>
        <div className="p-6">
          {n.cover_image && (
            <img src={`${API}/intranet/file/${n.cover_image}`} alt="" className="w-full max-h-72 object-cover rounded-lg mb-5" />
          )}
          {editingNews === n.id ? (
            <div className="space-y-3">
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="text-lg font-bold" data-testid="news-edit-title" />
              <Textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="min-h-[200px] text-sm" data-testid="news-edit-content" />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveEdit} data-testid="news-save-edit">Guardar</Button>
                <Button size="sm" variant="outline" onClick={() => setEditingNews(null)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-2" data-testid="news-detail-title">{n.title}</h2>
              <div className="flex items-center gap-3 text-xs text-slate-400 mb-4 pb-4 border-b">
                <span>Por: {n.created_by_name}</span>
                <span>{n.created_at?.slice(0, 10)}</span>
              </div>
              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="news-detail-content">
                {n.content || "Sin contenido adicional."}
              </div>
              {isAdmin && (
                <div className="flex gap-2 mt-6 pt-4 border-t">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(n)} data-testid="news-edit-btn"><PencilSimple size={14} className="mr-1" /> Editar</Button>
                  <label className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded-md text-slate-700 cursor-pointer hover:bg-slate-50">
                    <Camera size={14} /> Cambiar imagen
                    <input type="file" accept="image/*" onChange={e => handleUploadCover(n.id, e)} className="hidden" />
                  </label>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleDelete(n.id)} data-testid="news-delete-btn"><Trash size={14} className="mr-1" /> Eliminar</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="news-section" id="section-news">
      <div className="bg-blue-600 px-5 py-3 flex items-center justify-between">
        <h3 className="text-white font-bold flex items-center gap-2"><Newspaper size={18} weight="fill" /> Noticias y Comunicaciones</h3>
        {isAdmin && <button onClick={() => setShowForm(!showForm)} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full" data-testid="news-publish-btn"><Plus size={12} className="inline mr-1" />Publicar</button>}
      </div>
      {showForm && (
        <div className="p-4 border-b bg-blue-50 space-y-2">
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Titulo de la noticia..." className="text-sm" data-testid="news-title" />
          <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Contenido..." className="min-h-[60px] text-sm" data-testid="news-content" />
          <div className="flex gap-2"><Button size="sm" onClick={handleCreate} data-testid="news-submit">Publicar</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button></div>
        </div>
      )}
      <div className="p-4">
        {news.length === 0 ? <p className="text-center text-sm text-slate-400 py-6">Sin noticias publicadas</p> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {news.slice(0, 6).map(n => (
              <div key={n.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow group cursor-pointer" onClick={() => setSelectedNews(n)} data-testid={`news-${n.id}`}>
                {n.cover_image ? (
                  <img src={`${API}/intranet/file/${n.cover_image}`} alt="" className="w-full h-32 object-cover" />
                ) : (
                  <div className="w-full h-32 bg-gradient-to-br from-blue-100 to-slate-100 flex items-center justify-center"><Newspaper size={32} className="text-blue-300" /></div>
                )}
                <div className="p-3">
                  <h4 className="text-sm font-bold text-slate-800 line-clamp-2">{n.title}</h4>
                  {n.content && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.content}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-slate-400">{n.created_by_name} - {n.created_at?.slice(0, 10)}</span>
                    {isAdmin && (
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); handleEdit(n); setSelectedNews(n); }} className="text-blue-500 hover:text-blue-700"><PencilSimple size={14} /></button>
                        <label className="cursor-pointer text-blue-500 hover:text-blue-700" onClick={e => e.stopPropagation()}><Camera size={14} /><input type="file" accept="image/*" onChange={e => handleUploadCover(n.id, e)} className="hidden" /></label>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(n.id); }} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== DOCUMENT CENTER =====================
export function DocumentCenter() {
  const { token, user } = useAuth();
  const isAdmin = ["admin", "auxiliar"].includes(user?.role);
  const [docs, setDocs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Politicas", description: "" });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/intranet/documents`, { headers }); setDocs(data || []); }
    catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Titulo requerido");
    try { await axios.post(`${API}/intranet/documents`, form, { headers }); toast.success("Documento creado"); setForm({ title: "", category: "Politicas", description: "" }); setShowForm(false); load(); }
    catch (e) { toast.error("Error"); }
  };

  const handleUploadFile = async (docId, e) => {
    const file = e.target.files[0]; if (!file) return;
    try { const fd = new FormData(); fd.append("file", file); await axios.post(`${API}/intranet/documents/${docId}/file`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } }); toast.success("Archivo subido"); load(); }
    catch (er) { toast.error("Error al subir"); }
  };

  const categories = [...new Set(docs.map(d => d.category))];
  const CATS = ["Politicas", "Procedimientos", "Formatos", "General"];
  const allCats = [...new Set([...CATS, ...categories])];

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="document-center" id="section-docs">
      <div className="bg-amber-500 px-5 py-3 flex items-center justify-between">
        <h3 className="text-white font-bold flex items-center gap-2"><Folder size={18} weight="fill" /> Centro Documental</h3>
        {isAdmin && <button onClick={() => setShowForm(!showForm)} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full"><Plus size={12} className="inline mr-1" />Agregar</button>}
      </div>
      {showForm && (
        <div className="p-4 border-b bg-amber-50 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Titulo del documento" className="text-sm" data-testid="doc-title" />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="px-2 py-1.5 border rounded-lg text-sm" data-testid="doc-category">
              {CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex gap-2"><Button size="sm" onClick={handleCreate} data-testid="doc-submit">Crear</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button></div>
        </div>
      )}
      <div className="p-4">
        {docs.length === 0 ? <p className="text-center text-sm text-slate-400 py-6">Sin documentos</p> : (
          <div className="space-y-4">
            {allCats.filter(c => docs.some(d => d.category === c)).map(cat => (
              <div key={cat}>
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{cat}</h4>
                <div className="space-y-1">
                  {docs.filter(d => d.category === cat).map(d => (
                    <div key={d.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 group" data-testid={`doc-${d.id}`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <FileText size={18} className="text-amber-500 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{d.title}</p>
                          {d.filename && <p className="text-[10px] text-slate-400">{d.filename}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {d.file_id && <a href={`${API}/intranet/file/${d.file_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 p-1"><ArrowSquareOut size={14} /></a>}
                        {isAdmin && !d.file_id && <label className="cursor-pointer text-amber-500 hover:text-amber-700 p-1"><Camera size={14} /><input type="file" onChange={e => handleUploadFile(d.id, e)} className="hidden" /></label>}
                        {isAdmin && <button onClick={async () => { await axios.delete(`${API}/intranet/documents/${d.id}`, { headers }); load(); }} className="text-red-400 hover:text-red-600 p-1 opacity-0 group-hover:opacity-100"><Trash size={14} /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== TRAINING =====================
export function TrainingSection() {
  const { token, user } = useAuth();
  const isAdmin = ["admin", "auxiliar"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", url: "", type: "curso", category: "General" });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/intranet/training`, { headers }); setItems(data || []); }
    catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Titulo requerido");
    try { await axios.post(`${API}/intranet/training`, form, { headers }); toast.success("Contenido agregado"); setForm({ title: "", description: "", url: "", type: "curso", category: "General" }); setShowForm(false); load(); }
    catch (e) { toast.error("Error"); }
  };

  const typeColors = { curso: "bg-violet-100 text-violet-700", video: "bg-red-100 text-red-700", manual: "bg-blue-100 text-blue-700", informativo: "bg-slate-100 text-slate-700" };

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="training-section" id="section-training">
      <div className="bg-violet-600 px-5 py-3 flex items-center justify-between">
        <h3 className="text-white font-bold flex items-center gap-2"><GraduationCap size={18} weight="fill" /> Formacion y Capacitacion</h3>
        {isAdmin && <button onClick={() => setShowForm(!showForm)} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full"><Plus size={12} className="inline mr-1" />Agregar</button>}
      </div>
      {showForm && (
        <div className="p-4 border-b bg-violet-50 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Titulo" className="text-sm" data-testid="train-title" />
            <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="URL del recurso" className="text-sm" />
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="px-2 py-1.5 border rounded-lg text-sm">
              <option value="curso">Curso</option><option value="video">Video</option><option value="manual">Manual</option><option value="informativo">Informativo</option>
            </select>
          </div>
          <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descripcion..." className="min-h-[40px] text-sm" />
          <div className="flex gap-2"><Button size="sm" onClick={handleCreate} data-testid="train-submit">Agregar</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button></div>
        </div>
      )}
      <div className="p-4">
        {items.length === 0 ? <p className="text-center text-sm text-slate-400 py-6">Sin contenido formativo</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map(t => (
              <div key={t.id} className="border rounded-lg p-3 hover:bg-slate-50 group flex items-start gap-3" data-testid={`train-${t.id}`}>
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0"><GraduationCap size={20} className="text-violet-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-bold text-slate-800 truncate">{t.title}</span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${typeColors[t.type] || typeColors.informativo}`}>{t.type}</span>
                  </div>
                  {t.description && <p className="text-xs text-slate-500 line-clamp-1">{t.description}</p>}
                  {t.url && <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"><LinkIcon size={10} /> Ver recurso</a>}
                </div>
                {isAdmin && <button onClick={async () => { await axios.delete(`${API}/intranet/training/${t.id}`, { headers }); load(); }} className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 shrink-0"><Trash size={14} /></button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== SERVICES & TOOLS (links configurables) =====================
export function LinksSection({ type, title, icon: Icon, color, apiPath }) {
  const { token, user } = useAuth();
  const isAdmin = ["admin", "auxiliar"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", url: "" });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/intranet/${apiPath}`, { headers }); setItems(data || []); }
    catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token, apiPath]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Titulo requerido");
    try { await axios.post(`${API}/intranet/${apiPath}`, form, { headers }); toast.success("Agregado"); setForm({ title: "", description: "", url: "" }); setShowForm(false); load(); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid={`${type}-section`} id={`section-${type}`}>
      <div className={`${color} px-5 py-3 flex items-center justify-between`}>
        <h3 className="text-white font-bold flex items-center gap-2"><Icon size={18} weight="fill" /> {title}</h3>
        {isAdmin && <button onClick={() => setShowForm(!showForm)} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full"><Plus size={12} className="inline mr-1" />Agregar</button>}
      </div>
      {showForm && (
        <div className="p-4 border-b space-y-2" style={{ backgroundColor: "rgba(0,0,0,0.02)" }}>
          <div className="grid grid-cols-3 gap-2">
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Titulo" className="text-sm" data-testid={`${type}-title`} />
            <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descripcion" className="text-sm" />
            <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="URL" className="text-sm" />
          </div>
          <div className="flex gap-2"><Button size="sm" onClick={handleCreate} data-testid={`${type}-submit`}>Agregar</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button></div>
        </div>
      )}
      <div className="p-4">
        {items.length === 0 ? <p className="text-center text-sm text-slate-400 py-4">Sin elementos</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {items.map(item => (
              <a key={item.id} href={item.url || "#"} target={item.url ? "_blank" : undefined} rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 hover:shadow-sm transition-all group" data-testid={`${type}-${item.id}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color} bg-opacity-10`}><Icon size={18} className="text-white" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{item.title}</p>
                  {item.description && <p className="text-[10px] text-slate-500 truncate">{item.description}</p>}
                </div>
                <ArrowSquareOut size={14} className="text-slate-300 group-hover:text-blue-500 shrink-0" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== WELLNESS =====================
export function WellnessSection() {
  const { token, user } = useAuth();
  const isAdmin = ["admin", "auxiliar"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", url: "" });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/intranet/wellness`, { headers }); setItems(data || []); }
    catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Titulo requerido");
    try { await axios.post(`${API}/intranet/wellness`, form, { headers }); toast.success("Agregado"); setForm({ title: "", description: "", url: "" }); setShowForm(false); load(); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="wellness-section" id="section-wellness">
      <div className="bg-rose-500 px-5 py-3 flex items-center justify-between">
        <h3 className="text-white font-bold flex items-center gap-2"><Heart size={18} weight="fill" /> Bienestar y Convenios</h3>
        {isAdmin && <button onClick={() => setShowForm(!showForm)} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full"><Plus size={12} className="inline mr-1" />Agregar</button>}
      </div>
      {showForm && (
        <div className="p-4 border-b bg-rose-50 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Titulo del convenio" className="text-sm" data-testid="well-title" />
            <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="URL (opcional)" className="text-sm" />
          </div>
          <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descripcion del beneficio..." className="min-h-[40px] text-sm" />
          <div className="flex gap-2"><Button size="sm" onClick={handleCreate} data-testid="well-submit">Agregar</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button></div>
        </div>
      )}
      <div className="p-4">
        {items.length === 0 ? <p className="text-center text-sm text-slate-400 py-6">Sin convenios registrados</p> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {items.map(w => (
              <div key={w.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow group" data-testid={`well-${w.id}`}>
                {w.cover_image ? <img src={`${API}/intranet/file/${w.cover_image}`} alt="" className="w-full h-24 object-cover" /> : <div className="w-full h-24 bg-gradient-to-br from-rose-100 to-pink-50 flex items-center justify-center"><Heart size={28} className="text-rose-300" /></div>}
                <div className="p-3">
                  <h4 className="text-sm font-bold text-slate-800">{w.title}</h4>
                  {w.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{w.description}</p>}
                  {w.url && <a href={w.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block">Ver mas</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== PROJECTS =====================
export function ProjectsSection() {
  const { token, user } = useAuth();
  const isAdmin = ["admin", "auxiliar"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", status: "activo" });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/intranet/projects`, { headers }); setProjects(data || []); }
    catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Titulo requerido");
    try { await axios.post(`${API}/intranet/projects`, form, { headers }); toast.success("Proyecto creado"); setForm({ title: "", description: "", status: "activo" }); setShowForm(false); load(); }
    catch (e) { toast.error("Error"); }
  };

  const statusColors = { activo: "bg-emerald-100 text-emerald-700", pausado: "bg-amber-100 text-amber-700", completado: "bg-blue-100 text-blue-700" };

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="projects-section" id="section-projects">
      <div className="bg-cyan-600 px-5 py-3 flex items-center justify-between">
        <h3 className="text-white font-bold flex items-center gap-2"><Kanban size={18} weight="fill" /> Espacios de Trabajo</h3>
        {isAdmin && <button onClick={() => setShowForm(!showForm)} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full"><Plus size={12} className="inline mr-1" />Nuevo</button>}
      </div>
      {showForm && (
        <div className="p-4 border-b bg-cyan-50 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Nombre del proyecto" className="text-sm" data-testid="proj-title" />
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="px-2 py-1.5 border rounded-lg text-sm">
              <option value="activo">Activo</option><option value="pausado">Pausado</option><option value="completado">Completado</option>
            </select>
          </div>
          <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descripcion..." className="min-h-[40px] text-sm" />
          <div className="flex gap-2"><Button size="sm" onClick={handleCreate} data-testid="proj-submit">Crear</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button></div>
        </div>
      )}
      <div className="p-4">
        {projects.length === 0 ? <p className="text-center text-sm text-slate-400 py-6">Sin proyectos activos</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {projects.map(p => (
              <div key={p.id} className="border rounded-lg p-4 hover:bg-slate-50 group" data-testid={`proj-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-slate-800">{p.title}</h4>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${statusColors[p.status] || statusColors.activo}`}>{p.status}</span>
                </div>
                {p.description && <p className="text-xs text-slate-500 mb-3 line-clamp-2">{p.description}</p>}
                <div className="w-full bg-slate-200 rounded-full h-1.5"><div className="bg-cyan-500 h-1.5 rounded-full transition-all" style={{ width: `${p.progress || 0}%` }} /></div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] text-slate-400"><Users size={10} className="inline mr-0.5" />{(p.members || []).length} miembros</span>
                  <span className="text-[10px] font-bold text-cyan-700">{p.progress || 0}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== FORUM =====================
export function ForumSection() {
  const { token, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "general" });
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/intranet/forum`, { headers }); setPosts(data || []); }
    catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Titulo requerido");
    try { await axios.post(`${API}/intranet/forum`, form, { headers }); toast.success("Publicado"); setForm({ title: "", content: "", category: "general" }); setShowForm(false); load(); }
    catch (e) { toast.error("Error"); }
  };

  const handleComment = async (postId) => {
    if (!replyContent.trim()) return;
    try { await axios.post(`${API}/intranet/forum/${postId}/comment`, { content: replyContent }, { headers }); toast.success("Comentario agregado"); setReplyTo(null); setReplyContent(""); load(); }
    catch (e) { toast.error("Error"); }
  };

  const catColors = { general: "bg-slate-100 text-slate-600", idea: "bg-amber-100 text-amber-700", pregunta: "bg-blue-100 text-blue-700", propuesta: "bg-emerald-100 text-emerald-700" };

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="forum-section" id="section-forum">
      <div className="bg-orange-500 px-5 py-3 flex items-center justify-between">
        <h3 className="text-white font-bold flex items-center gap-2"><ChatCircle size={18} weight="fill" /> Foro e Ideas</h3>
        <button onClick={() => setShowForm(!showForm)} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full"><Plus size={12} className="inline mr-1" />Nueva Idea</button>
      </div>
      {showForm && (
        <div className="p-4 border-b bg-orange-50 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Titulo" className="text-sm col-span-2" data-testid="forum-title" />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="px-2 py-1.5 border rounded-lg text-sm">
              <option value="general">General</option><option value="idea">Idea</option><option value="pregunta">Pregunta</option><option value="propuesta">Propuesta</option>
            </select>
          </div>
          <Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Comparte tu idea, pregunta o propuesta..." className="min-h-[60px] text-sm" data-testid="forum-content" />
          <div className="flex gap-2"><Button size="sm" onClick={handleCreate} data-testid="forum-submit">Publicar</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button></div>
        </div>
      )}
      <div className="p-4 space-y-3">
        {posts.length === 0 ? <p className="text-center text-sm text-slate-400 py-6">Se el primero en publicar una idea</p> : (
          posts.slice(0, 10).map(p => (
            <div key={p.id} className="border rounded-lg p-4" data-testid={`forum-${p.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-bold text-slate-800">{p.title}</span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${catColors[p.category] || catColors.general}`}>{p.category}</span>
                  </div>
                  <p className="text-[10px] text-slate-400">{p.author_name} ({p.author_role}) - {p.created_at?.slice(0, 10)}</p>
                </div>
                <span className="text-[10px] text-slate-400 flex items-center gap-1 shrink-0"><ChatText size={12} /> {(p.comments || []).length}</span>
              </div>
              {p.content && <p className="text-xs text-slate-600 mb-3 whitespace-pre-wrap">{p.content}</p>}
              {/* Comments */}
              {(p.comments || []).length > 0 && (
                <div className="ml-4 border-l-2 border-slate-200 pl-3 space-y-2 mb-3">
                  {(p.comments || []).slice(-3).map(c => (
                    <div key={c.id} className="text-xs">
                      <span className="font-semibold text-slate-700">{c.author_name}</span>
                      <span className="text-slate-400 ml-1">{c.created_at?.slice(0, 10)}</span>
                      <p className="text-slate-600 mt-0.5">{c.content}</p>
                    </div>
                  ))}
                </div>
              )}
              {/* Reply */}
              {replyTo === p.id ? (
                <div className="flex gap-2 mt-2">
                  <Input value={replyContent} onChange={e => setReplyContent(e.target.value)} placeholder="Escribe un comentario..." className="text-xs flex-1" data-testid={`reply-${p.id}`} onKeyDown={e => e.key === "Enter" && handleComment(p.id)} />
                  <Button size="sm" onClick={() => handleComment(p.id)} data-testid={`reply-send-${p.id}`}><PaperPlaneTilt size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => { setReplyTo(null); setReplyContent(""); }}><X size={14} /></Button>
                </div>
              ) : (
                <button onClick={() => setReplyTo(p.id)} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><ChatText size={12} /> Comentar</button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
